import { useState, useEffect } from "react";
import {
  View,
  Text,
  StyleSheet,
  FlatList,
  Alert,
  Platform,
  ActivityIndicator,
} from "react-native";
import SelectableItemWithCheckbox from "../../components/SelectableItemWithCheckbox";
import ConfirmButton from "../../components/ConfirmButton";
import {
  getCleanNames,
  getCleanNameFromId,
  getCleanIdFromName,
} from "../../utils/helpers/helpers";
import { useSelector, useDispatch } from "react-redux";
import { buildingListActions } from "../../store/building-slice";
import { useDropsOnCleanChange } from "../../hooks/useBuildingData";
import { firebaseApp } from "../../firebase/config";
import { getFirestore, doc, getDoc, updateDoc, serverTimestamp, arrayUnion, Timestamp } from "firebase/firestore";
import Swal from "sweetalert2";

const CleanAndDropSelectionScreen = ({ navigation }) => {
  const [selectedDrop, setSelectedDrop] = useState(null);
  const [cleanNames, setCleanNames] = useState([]);

  const selectedBuilding = useSelector(
    (state) => state.buildingList.selectedBuilding
  );
  const selectedSite = selectedBuilding?.site_id;
  const building = useSelector((state) => state.buildingList.buildingData);
  const currentClean = useSelector((state) => state.buildingList.currentClean);
  const drops = useSelector((state) => state.buildingList.drops);
  const userUid = useSelector((state) => state.auth.uid);
  const dispatch = useDispatch();

  const { dropsLoading, loadDropsForClean } = useDropsOnCleanChange(selectedBuilding, {
    officialClean: building?.currentClean,
    noDrops: building?.noDrops,
  });

  const db = getFirestore(firebaseApp);

  useEffect(() => {
    if (building) {
      setCleanNames(getCleanNames(building.cleans));
    }
  }, [building]);

  useEffect(() => {
    setSelectedDrop(null);
  }, [currentClean]);

  const toDateTimeLocalValue = (dateValue) => {
    const date = (dateValue && typeof dateValue.toDate === "function") 
      ? dateValue.toDate() 
      : (dateValue instanceof Date ? dateValue : new Date());
    const year = date.getFullYear();
    const month = `${date.getMonth() + 1}`.padStart(2, "0");
    const day = `${date.getDate()}`.padStart(2, "0");
    const hours = `${date.getHours()}`.padStart(2, "0");
    const minutes = `${date.getMinutes()}`.padStart(2, "0");
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  const saveUpdatedCleanDate = async (item, newDate) => {
    try {
      const dropRef = doc(
        db,
        "BuildingsX",
        selectedSite,
        "currentYear",
        currentClean,
        "drops",
        item.name
      );

      await updateDoc(dropRef, {
        date: Timestamp.fromDate(newDate),
        updatedAt: serverTimestamp(),
        updatedBy: userUid,
        auditLogs: arrayUnion({
          action: "edit_clean_date",
          editedAt: new Date(),
          editedBy: userUid,
          previousDate: item.date || null,
          newDate: Timestamp.fromDate(newDate),
        }),
      });

      // Reload the drops list to immediately update the UI with the fresh date
      await loadDropsForClean(currentClean);

      if (Platform.OS === "web") {
        Swal.fire({
          icon: "success",
          title: "Saved!",
          text: `The clean date for ${item.name} has been updated successfully.`,
          confirmButtonColor: "#5e99fa",
        });
      } else {
        Alert.alert("Success", `The clean date for ${item.name} has been updated.`);
      }
    } catch (error) {
      console.error("Error saving updated clean date:", error);
      if (Platform.OS === "web") {
        Swal.fire({
          icon: "error",
          title: "Error",
          text: "Failed to update the clean date. Please try again.",
        });
      } else {
        Alert.alert("Error", "Failed to update the clean date.");
      }
    }
  };

  const selectItem = (item, type) => {
    if (type === "clean") {
      dispatch(
        buildingListActions.setCurrentClean({ clean: getCleanIdFromName(item) })
      );
    }
    if (type === "drop") {
      if (item.isComplete) {
        setSelectedDrop(null);
        const dateStr = item.currentDate || "an unknown date";
        
        if (Platform.OS === "web") {
          Swal.fire({
            title: `${item.name} is already cleaned!`,
            text: `It was cleaned on ${dateStr}. Would you like to update the cleaning date?`,
            icon: "info",
            showCancelButton: true,
            confirmButtonText: "Update Date",
            cancelButtonText: "Close",
            confirmButtonColor: "#5e99fa",
            cancelButtonColor: "#aaa",
          }).then(async (result) => {
            if (result.isConfirmed) {
              const dateResult = await Swal.fire({
                title: "Select new cleaning date",
                input: "datetime-local",
                inputValue: toDateTimeLocalValue(item.date),
                showCancelButton: true,
                confirmButtonText: "Save Date",
                cancelButtonText: "Cancel",
                confirmButtonColor: "#5e99fa",
              });

              if (dateResult.isConfirmed && dateResult.value) {
                const selectedDate = new Date(dateResult.value);
                if (!Number.isNaN(selectedDate.getTime())) {
                  await saveUpdatedCleanDate(item, selectedDate);
                }
              }
            }
          });
        } else {
          Alert.alert(
            "Already Cleaned",
            `${item.name} was cleaned on ${dateStr}. Date editing is available in web PWA mode.`,
            [{ text: "OK" }]
          );
        }
        return;
      }
      setSelectedDrop(item);
    }
  };

  const confirmSelection = async () => {
    try {
      if (selectedDrop.equipmentId) {
        const equipmentStatus = await getEquipmentStatus(
          selectedDrop.equipmentId
        );
        if (!equipmentStatus) {
          // throw new Error('Equipment is currenly out of service. Please fill a defect form.')
          // Swal with button to redirent to defect form
          return await Swal.fire({
            title: "Equipment is currenly out of service",
            text: "Please fill a defect form",
            icon: "warning",
            showCancelButton: true,
            confirmButtonText: "Ok",
            cancelButtonText: "Cancel",
            confirmButtonColor: "#5e99fa",
            cancelButtonColor: "#d33",
          }).then((result) => {
            if (result.isConfirmed) {
              navigation.navigate("Defects", {
                siteId: selectedSite,
              });
            }
          });
        }
      }
      dispatch(
        buildingListActions.setCurrentDrop({
          drop: selectedDrop,
        })
      );
      navigation.navigate("Clean");
    } catch (error) {
      // alert(error)
      console.log(error);
    }
  };

  const getEquipmentStatus = async (equipmentId) => {
    const equipmentRef = doc(
      db,
      "BuildingsX",
      selectedSite,
      "equipment",
      equipmentId
    );
    const equipmentSnapshot = await getDoc(equipmentRef);
    return equipmentSnapshot.data().inService;
  };

  if (!drops?.length && !cleanNames.length)
    return (
      <ActivityIndicator style={{ flex: 1 }} size="large" color="#5e99fa" />
    );

  return (
    <View style={styles.container}>
      <View style={styles.buildingNameBox}>
        <Text style={styles.buildingNameText}>{building.name}</Text>
      </View>

      <View style={styles.cleansBox}>
        <Text style={styles.sectionTitle}>Select Clean</Text>
        <FlatList
          data={cleanNames}
          renderItem={({ item }) => (
            <SelectableItemWithCheckbox
              name={item}
              selectItem={() => selectItem(item, "clean")}
              isSelected={getCleanNameFromId(currentClean)}
            />
          )}
          keyExtractor={(item, index) => `${item}-${index}`}
          nestedScrollEnabled
        />
      </View>

      <View style={styles.dropsBox}>
        <Text style={styles.sectionTitle}>Select Drop</Text>
        {dropsLoading ? (
          <ActivityIndicator size="small" color="#5e99fa" />
        ) : null}
        <FlatList
          data={drops && drops}
          extraData={currentClean}
          renderItem={({ item }) => (
            <SelectableItemWithCheckbox
              name={item.name}
              selectItem={() => selectItem(item, "drop")}
              isSelected={selectedDrop && selectedDrop.name}
              isCleaned={item.isComplete}
            />
          )}
          keyExtractor={(item, index) => index}
          nestedScrollEnabled
        />
      </View>

      {selectedDrop && <ConfirmButton confirmSelection={confirmSelection} />}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    marginVertical: 20,
    marginHorizontal: 20,
  },
  buildingNameBox: {
    backgroundColor: "#5e99fa",
    borderRadius: 10,
    justifyContent: "center",
    paddingLeft: 10,
    flex: 0.5,
  },
  buildingNameText: {
    color: "#fff",
    fontWeight: "600",
  },
  cleansBox: {
    marginVertical: 20,
    flex: 2,
  },
  dropsBox: {
    marginTop: 10,
    flex: 3,
  },
  sectionTitle: {
    fontSize: 20,
    fontWeight: "700",
    paddingBottom: 10,
  },
});

export default CleanAndDropSelectionScreen;
